import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/shared/lib/utils";

/**
 * Grid-based tables (design-system.md §4 → Tables). Rows are CSS grid, wide
 * cells use `col-span-*`. The whole table scrolls horizontally inside its own
 * container rather than breaking the layout.
 */
export function TableCard({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("overflow-x-auto rounded-ge-card border border-ge-border-subtle", className)}>
      <div className="min-w-[720px]">{children}</div>
    </div>
  );
}

interface RowProps extends HTMLAttributes<HTMLDivElement> {
  /** Tailwind grid-template-columns class, e.g. "grid-cols-8". */
  grid: string;
}

/** Header row: sidebar bg, faint mono uppercase cells. */
export function TableHeadRow({ grid, className, children, ...props }: RowProps) {
  return (
    <div
      className={cn(
        "grid gap-2 border-b border-ge-border-subtle bg-ge-sidebar px-4 py-2",
        grid,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Data row: bottom hairline, 13px cells. */
export function TableRow({ grid, className, children, ...props }: RowProps) {
  return (
    <div
      className={cn(
        "grid items-center gap-2 border-b border-ge-border-row px-4 py-[11px] last:border-b-0",
        grid,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Header cell. */
export function Th({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("font-mono text-[10px] uppercase tracking-ge-table text-ge-faint", className)}>
      {children}
    </div>
  );
}

/** The `--` empty cell. */
export function Empty() {
  return <span className="font-mono text-[11px] text-ge-faint">--</span>;
}
